import React, { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useAppContext } from '@/hooks/useAppContext';
import { useTheme } from '@/hooks/useTheme';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { usePublishDMRelayList } from '@/hooks/useSendMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useNWCContext } from '@/contexts/NWCContext';
import { useWallet } from '@/hooks/useWallet';
import { useNip85List, addTrustSource, removeTrustSource, getTrustSources } from '@/hooks/useNip85Trust';
import { ChevronRight, Shield, Globe, Users, Trash, Mail, Plus, Wallet, Link as LinkIcon, Sparkles, Ban, EyeOff, ShieldCheck } from 'lucide-react';

export function SettingsPage() {
  useSeoMeta({
    title: 'Settings / Twittstr',
    description: 'Customize layout, clear cached storage, and configure decentralized relay networks.',
  });

  const { theme, setTheme } = useTheme();
  const { config, updateConfig } = useAppContext();
  const { user } = useCurrentUser();
  const { toast } = useToast();

  const handleClearCache = () => {
    localStorage.clear();
    toast({ title: 'Cache Cleared 🧹', description: 'Settings restored to factory template defaults!' });
    setTimeout(() => {
      window.location.reload();
    }, 1200);
  };

  return (
    <div className="flex flex-col w-full min-h-screen bg-background text-foreground">
      {/* Settings Title */}
      <header className="sticky top-0 bg-background/85 backdrop-blur-md z-30 border-b border-border p-4">
        <span className="font-extrabold text-xl text-foreground">Settings</span>
      </header>

      {/* Main Settings Body */}
      <div className="flex flex-col select-none divide-y divide-border">
        
        {/* Wallet connection panel */}
        {user && (
          <SettingsWalletPanel />
        )}

        {/* DM Relays Settings Panel */}
        {user && (
          <SettingsDMRelays />
        )}

        {/* Content Filters - Muted Words and Users */}
        {user && (
          <SettingsContentFilters />
        )}

        {/* Trust/Mute Lists - NIP-85 Subscription */}
        {user && (
          <SettingsTrustSources />
        )}

        {/* Theme Settings row banner */}
        <div className="p-4 flex flex-col gap-3">
          <span className="font-extrabold text-[15px] text-foreground">Customize your view</span>
          <p className="text-xs text-muted-foreground leading-normal">
            Manage your font, color, and background options. These settings affect all accounts logged in on this browser.
          </p>

          {/* Theme custom pill toggle controls */}
          <div className="grid grid-cols-3 gap-2 bg-secondary/30 p-1.5 rounded-full border border-border mt-1">
            <button
              onClick={() => setTheme('light')}
              className={`py-2 rounded-full font-bold text-center cursor-pointer transition-all text-sm ${theme === 'light' ? 'bg-background text-sky-500 shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Default Light
            </button>
            <button
              onClick={() => setTheme('dim')}
              className={`py-2 rounded-full font-bold text-center cursor-pointer transition-all text-sm ${theme === 'dim' ? 'bg-background text-sky-500 shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Dim
            </button>
            <button
              onClick={() => setTheme('dark')}
              className={`py-2 rounded-full font-bold text-center cursor-pointer transition-all text-sm ${theme === 'dark' ? 'bg-background text-sky-500 shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Lights out
            </button>
          </div>
        </div>

        {/* Relay Servers Config */}
        <div className="p-4 flex flex-col gap-2">
          <div className="flex items-center gap-2 font-extrabold text-[15px]">
            <Globe className="size-4.5 text-sky-500" />
            <span>Relay Networks</span>
          </div>
          <p className="text-xs text-muted-foreground leading-normal mb-2">
            Nostr is permissionless. Check and monitor active relay endpoints synced below to assure live connectivity content flows.
          </p>

          <div className="flex flex-col gap-2.5 rounded-2xl bg-secondary/15 border border-border/80 p-4">
            {config.relayMetadata.relays.map((relay, index) => (
              <div key={index} className="flex items-center justify-between font-mono text-xs leading-none border-b border-border/40 pb-2 last:border-b-0 last:pb-0">
                <span className="truncate text-foreground/80 max-w-[280px]">{relay.url}</span>
                <span className="px-2 py-0.5 rounded-full bg-teal-500/10 text-teal-600 font-semibold text-[10px]">Read/Write Connected</span>
              </div>
            ))}
          </div>
        </div>

        {/* Security & Client Details */}
        <div className="p-4 flex flex-col gap-2">
          <div className="flex items-center gap-2 font-extrabold text-[15px]">
            <Shield className="size-4.5 text-sky-500" />
            <span>Twittstr Client Security</span>
          </div>
          <p className="text-xs text-muted-foreground leading-normal mb-1">
            This client is 100% serverless. Your secret keys (`nsec`) exist only in your browser storage or connected extensions.
          </p>
          <div className="flex items-center justify-between text-xs py-2">
            <span className="text-muted-foreground">Session status:</span>
            <span className={`font-bold ${user ? 'text-teal-600' : 'text-amber-500'}`}>
              {user ? 'Authenticated and Signed' : 'Read-only / Guest mode'}
            </span>
          </div>
        </div>

        {/* Restore / Storage Utilities */}
        <div className="p-4 flex flex-col gap-3">
          <span className="font-extrabold text-[15px] text-foreground">Advanced Utilities</span>
          <Button
            onClick={handleClearCache}
            variant="destructive"
            className="rounded-full w-full font-bold flex items-center justify-center gap-2 h-10 mt-1 cursor-pointer"
          >
            <Trash className="size-4" />
            <span>Reset Cache & Clear Database</span>
          </Button>
        </div>

      </div>
    </div>
  );
}

function SettingsWalletPanel() {
  const { connections, addConnection, removeConnection } = useNWCContext();
  const { preferredMethod } = useWallet();
  const [nwcInput, setNwcInput] = useState('');
  const { toast } = useToast();

  const handleConnectWallet = async () => {
    if (!nwcInput.trim()) return;
    const success = await addConnection(nwcInput.trim());
    if (success) {
      toast({ title: 'Wallet Connected ⚡', description: 'NWC connection established successfully!' });
      setNwcInput('');
    } else {
      toast({ title: 'Connection Rejected', description: 'String must be a valid nostr+walletconnect:// URI.', variant: 'destructive' });
    }
  };

  return (
    <div className="p-4 flex flex-col gap-2.5">
      <div className="flex items-center gap-2 font-extrabold text-[15px]">
        <Wallet className="size-4.5 text-sky-500" />
        <span>Lightning Wallet (NWC / NIP-47)</span>
      </div>
      <p className="text-xs text-muted-foreground leading-normal mb-1">
        Configure wallet integrations. Connect your preferred Lightning node using Nostr Wallet Connect parameters to activate one-tap tipping on Twittstr.
      </p>

      {connections.length > 0 ? (
        <div className="flex flex-col gap-2 rounded-2xl bg-secondary/15 border border-border/80 p-4">
          {connections.map((c, index) => (
            <div key={index} className="flex items-center justify-between font-mono text-xs leading-none">
              <span className="truncate text-foreground/80 max-w-[210px]">{c.connectionString}</span>
              <button 
                onClick={() => removeConnection(c.connectionString)}
                className="text-xs font-bold text-rose-500 hover:underline cursor-pointer"
              >
                Disconnect
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-3 bg-secondary/10 border border-dashed border-border rounded-xl text-center text-xs text-muted-foreground">
          No wallet connected. All creator tips will use secure QR code fallback.
        </div>
      )}

      <div className="flex gap-2 items-center mt-1">
        <Input 
          type="text" 
          placeholder="nostr+walletconnect://..." 
          className="flex-1 h-9 rounded-full bg-secondary/40 border-none pl-3 text-xs"
          value={nwcInput}
          onChange={(e) => setNwcInput(e.target.value)}
        />
        <Button 
          type="button" 
          onClick={handleConnectWallet}
          className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold h-9 px-4 shrink-0 cursor-pointer text-xs"
        >
          Connect
        </Button>
      </div>
    </div>
  );
}

function SettingsDMRelays() {
  const publishDMList = usePublishDMRelayList();
  const [newInboxUrl, setNewInboxUrl] = useState('');
  const [inboxes, setInboxes] = useState<string[]>([
    'wss://relay.ditto.pub',
    'wss://relay.primal.net',
    'wss://relay.damus.io'
  ]);

  const handleAdd = () => {
    if (!newInboxUrl.trim()) return;
    const url = newInboxUrl.trim();
    if (!inboxes.includes(url)) {
      setInboxes(prev => [...prev, url]);
    }
    setNewInboxUrl('');
  };

  const handleRemove = (url: string) => {
    setInboxes(prev => prev.filter(i => i !== url));
  };

  const handlePublish = () => {
    publishDMList.mutate(inboxes);
  };

  return (
    <div className="p-4 flex flex-col gap-2.5">
      <div className="flex items-center gap-2 font-extrabold text-[15px]">
        <Mail className="size-4.5 text-sky-500" />
        <span>DM Relays (NIP-17 / kind 10050)</span>
      </div>
      <p className="text-xs text-muted-foreground leading-normal mb-1">
        Configure specialized DM inbox servers. Senders on Twittstr (and other compatible clients like Damus/Amethyst) will query this list to find where to route your private gift-wrapped envelopes.
      </p>

      {/* Relays List */}
      <div className="flex flex-col gap-2.5 rounded-2xl bg-secondary/15 border border-border/80 p-4">
        {inboxes.map((url) => (
          <div key={url} className="flex items-center justify-between font-mono text-xs leading-none">
            <span className="truncate text-foreground/80">{url}</span>
            <button 
              type="button" 
              onClick={() => handleRemove(url)}
              className="text-[10px] text-rose-500 font-bold hover:underline cursor-pointer"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-2 items-center mt-1">
        <Input 
          type="text" 
          placeholder="wss://inbox-relay..." 
          className="flex-1 h-9 rounded-full bg-secondary/40 border-none pl-3 text-xs"
          value={newInboxUrl}
          onChange={(e) => setNewInboxUrl(e.target.value)}
        />
        <Button 
          type="button" 
          onClick={handleAdd}
          size="sm" 
          className="rounded-full bg-secondary/80 hover:bg-secondary border border-border/50 text-foreground font-semibold h-9 px-3 shrink-0 cursor-pointer"
        >
          Add
        </Button>
      </div>

      <Button
        onClick={handlePublish}
        disabled={publishDMList.isPending}
        className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold h-9 text-xs w-full mt-2 cursor-pointer"
      >
        {publishDMList.isPending ? 'Publishing...' : 'Publish DM Setup'}
      </Button>
    </div>
  );
}

function SettingsContentFilters() {
  const { toast } = useToast();
  const [mutedWords, setMutedWords] = useState<string[]>(
    JSON.parse(localStorage.getItem('muted-words') || '[]')
  );
  const [mutedPubkeys, setMutedPubkeys] = useState<string[]>(
    JSON.parse(localStorage.getItem('muted-pubkeys') || '[]')
  );
  const [cwPolicy, setCwPolicy] = useState<string>(
    localStorage.getItem('content-warning-policy') || 'blur'
  );
  const [langPref, setLangPref] = useState<string>(
    localStorage.getItem('preferred-language') || 'all'
  );
  const [newWord, setNewWord] = useState('');
  const [newPubkey, setNewPubkey] = useState('');

  const saveFilters = (words: string[], pubkeys: string[]) => {
    localStorage.setItem('muted-words', JSON.stringify(words));
    localStorage.setItem('muted-pubkeys', JSON.stringify(pubkeys));
    toast({ title: 'Filters Updated', description: 'Your content filters have been saved.' });
  };

  const handleCwPolicyChange = (policy: string) => {
    setCwPolicy(policy);
    localStorage.setItem('content-warning-policy', policy);
    toast({ title: 'Content Warning Policy Updated', description: `Content warnings will now be ${policy === 'blur' ? 'blurred' : policy === 'hide' ? 'hidden' : 'shown'} by default.` });
  };

  const handleLangChange = (lang: string) => {
    setLangPref(lang);
    localStorage.setItem('preferred-language', lang);
    toast({ title: 'Language Filter Updated', description: lang === 'all' ? 'Showing all languages' : `Showing only ${lang} content` });
  };

  const handleAddWord = () => {
    const word = newWord.trim().toLowerCase();
    if (!word || mutedWords.includes(word)) return;
    const updated = [...mutedWords, word];
    setMutedWords(updated);
    setNewWord('');
    saveFilters(updated, mutedPubkeys);
  };

  const handleRemoveWord = (word: string) => {
    const updated = mutedWords.filter(w => w !== word);
    setMutedWords(updated);
    saveFilters(updated, mutedPubkeys);
  };

  const handleAddPubkey = () => {
    const pubkey = newPubkey.trim();
    if (!pubkey || mutedPubkeys.includes(pubkey)) return;
    const updated = [...mutedPubkeys, pubkey];
    setMutedPubkeys(updated);
    setNewPubkey('');
    saveFilters(mutedWords, updated);
  };

  const handleRemovePubkey = (pubkey: string) => {
    const updated = mutedPubkeys.filter(p => p !== pubkey);
    setMutedPubkeys(updated);
    saveFilters(mutedWords, updated);
  };

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2 font-extrabold text-[15px]">
        <EyeOff className="size-4.5 text-sky-500" />
        <span>Content Filters</span>
      </div>
      <p className="text-xs text-muted-foreground leading-normal">
        Manage how content is filtered in your For You feed. Trust graph (people you follow) always takes priority.
      </p>

      {/* Content Warning Policy */}
      <div className="flex flex-col gap-2 mt-1">
        <span className="text-xs font-bold text-muted-foreground">Content Warnings (NIP-36)</span>
        <p className="text-[10px] text-muted-foreground">How to handle posts with content warnings:</p>
        <div className="grid grid-cols-3 gap-2 bg-secondary/30 p-1.5 rounded-full border border-border/60">
          {[
            { key: 'blur', label: 'Blur' },
            { key: 'show', label: 'Show' },
            { key: 'hide', label: 'Hide' },
          ].map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => handleCwPolicyChange(opt.key)}
              className={`py-1.5 rounded-full font-bold text-xs text-center cursor-pointer transition-all ${
                cwPolicy === opt.key
                  ? 'bg-background text-sky-500 shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Language Filter */}
      <div className="flex flex-col gap-2 mt-1">
        <span className="text-xs font-bold text-muted-foreground">Language Filter</span>
        <p className="text-[10px] text-muted-foreground">Show only posts in a preferred language:</p>
        <div className="flex gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'en', label: 'English' },
            { key: 'zh', label: '中文' },
            { key: 'ru', label: 'Русский' },
            { key: 'ar', label: 'العربية' },
          ].map(lang => (
            <button
              key={lang.key}
              type="button"
              onClick={() => handleLangChange(lang.key)}
              className={`py-1.5 px-3 rounded-full font-bold text-xs cursor-pointer transition-all ${
                langPref === lang.key
                  ? 'bg-foreground text-background'
                  : 'bg-secondary/40 text-muted-foreground hover:text-foreground'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      </div>

      {/* Muted Words */}
      <div className="flex flex-col gap-2 mt-1">
        <span className="text-xs font-bold text-muted-foreground">Muted Words</span>
        <div className="flex gap-2 items-center">
          <Input
            type="text"
            placeholder="Add a word to mute..."
            className="flex-1 h-9 rounded-full bg-secondary/40 border-none pl-3 text-xs"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddWord()}
          />
          <Button
            type="button"
            onClick={handleAddWord}
            size="sm"
            className="rounded-full bg-secondary/80 hover:bg-secondary border border-border/50 text-foreground font-semibold h-9 px-3 shrink-0 cursor-pointer"
          >
            Add
          </Button>
        </div>
        {mutedWords.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {mutedWords.map(word => (
              <button
                key={word}
                type="button"
                onClick={() => handleRemoveWord(word)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary/30 border border-border/50 text-xs text-foreground hover:bg-secondary/50 transition-colors cursor-pointer"
              >
                <span>{word}</span>
                <span className="text-muted-foreground">×</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Muted Users */}
      <div className="flex flex-col gap-2 mt-1">
        <span className="text-xs font-bold text-muted-foreground">Muted Users (npub)</span>
        <div className="flex gap-2 items-center">
          <Input
            type="text"
            placeholder="npub1..."
            className="flex-1 h-9 rounded-full bg-secondary/40 border-none pl-3 text-xs font-mono"
            value={newPubkey}
            onChange={(e) => setNewPubkey(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddPubkey()}
          />
          <Button
            type="button"
            onClick={handleAddPubkey}
            size="sm"
            className="rounded-full bg-secondary/80 hover:bg-secondary border border-border/50 text-foreground font-semibold h-9 px-3 shrink-0 cursor-pointer"
          >
            Add
          </Button>
        </div>
        {mutedPubkeys.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-1">
            {mutedPubkeys.map(pk => (
              <div
                key={pk}
                className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-secondary/20 border border-border/40"
              >
                <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[200px]">
                  {pk.slice(0, 12)}...{pk.slice(-6)}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemovePubkey(pk)}
                  className="text-xs text-rose-500 hover:text-rose-400 font-bold cursor-pointer shrink-0 ml-2"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsTrustSources() {
  const { toast } = useToast();
  const [newSourcePubkey, setNewSourcePubkey] = useState('');
  const [newSourceLabel, setNewSourceLabel] = useState('');
  const [newSourceKind, setNewSourceKind] = useState<'trust' | 'mute'>('trust');
  const sources = getTrustSources();

  const handleAddSource = () => {
    const pubkey = newSourcePubkey.trim();
    if (!pubkey) return;
    addTrustSource({ pubkey, label: newSourceLabel || `Source ${pubkey.slice(0, 8)}`, kind: newSourceKind });
    setNewSourcePubkey('');
    setNewSourceLabel('');
    toast({ title: 'Source Added', description: `Now fetching ${newSourceKind} list from this pubkey.` });
  };

  const handleRemoveSource = (pubkey: string) => {
    removeTrustSource(pubkey);
    toast({ title: 'Source Removed', description: 'Trust/mute source removed from your subscriptions.' });
  };

  const handleExportTrust = () => {
    const data = JSON.stringify(sources, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'twittstr-trust-sources.json';
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Exported', description: 'Your trust sources have been exported.' });
  };

  const handleImportTrust = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target?.result as string);
        if (Array.isArray(imported)) {
          imported.forEach((s: any) => {
            if (s.pubkey && s.kind) {
              addTrustSource({ pubkey: s.pubkey, label: s.label || '', kind: s.kind });
            }
          });
          toast({ title: 'Imported', description: `Imported ${imported.length} trust sources.` });
        }
      } catch {
        toast({ title: 'Import Failed', description: 'Invalid trust sources file.', variant: 'destructive' });
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2 font-extrabold text-[15px]">
        <ShieldCheck className="size-4.5 text-sky-500" />
        <span>Trust System (NIP-85)</span>
      </div>
      <p className="text-xs text-muted-foreground leading-normal">
        Subscribe to trusted moderators' mute/trust lists. Their curated lists will be applied to your feed automatically.
        Trust lists boost accounts you might want to follow; mute lists hide spam accounts they've already flagged.
      </p>

      {/* Current Sources */}
      <div className="flex flex-col gap-2">
        <span className="text-xs font-bold text-muted-foreground">Active Subscriptions</span>
        {sources.length > 0 ? (
          <div className="flex flex-col gap-2 rounded-2xl bg-secondary/15 border border-border/80 p-3">
            {sources.map((source) => (
              <div key={source.pubkey} className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-bold truncate block">{source.label}</span>
                  <span className="text-[10px] font-mono text-muted-foreground truncate block">
                    {source.pubkey.slice(0, 12)}...
                    <span className={`ml-1.5 px-1 py-0.5 rounded-full text-[8px] font-semibold ${
                      source.kind === 'trust' ? 'bg-teal-500/10 text-teal-600' : 'bg-rose-500/10 text-rose-600'
                    }`}>
                      {source.kind === 'trust' ? 'Trust' : 'Mute'}
                    </span>
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveSource(source.pubkey)}
                  className="text-xs text-rose-500 hover:text-rose-400 font-bold cursor-pointer shrink-0 ml-2"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-3 bg-secondary/10 border border-dashed border-border rounded-xl text-center text-xs text-muted-foreground">
            No subscriptions yet. Add a trusted moderator's pubkey below.
          </div>
        )}
      </div>

      {/* Add Source */}
      <div className="flex flex-col gap-2 mt-1">
        <span className="text-xs font-bold text-muted-foreground">Add a Trust Source</span>
        <div className="flex gap-1.5 mb-1">
          {['trust', 'mute'].map(kind => (
            <button
              key={kind}
              type="button"
              onClick={() => setNewSourceKind(kind as 'trust' | 'mute')}
              className={`py-1 px-3 rounded-full text-[10px] font-bold cursor-pointer transition-all capitalize ${
                newSourceKind === kind
                  ? 'bg-foreground text-background'
                  : 'bg-secondary/40 text-muted-foreground'
              }`}
            >
              {kind} List
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <Input
            type="text"
            placeholder="Source npub..."
            className="flex-1 h-9 rounded-full bg-secondary/40 border-none pl-3 text-xs font-mono"
            value={newSourcePubkey}
            onChange={(e) => setNewSourcePubkey(e.target.value)}
          />
          <Input
            type="text"
            placeholder="Label"
            className="w-20 h-9 rounded-full bg-secondary/40 border-none pl-3 text-xs"
            value={newSourceLabel}
            onChange={(e) => setNewSourceLabel(e.target.value)}
          />
          <Button
            type="button"
            onClick={handleAddSource}
            size="sm"
            className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold h-9 px-3 shrink-0 cursor-pointer text-xs"
          >
            Add
          </Button>
        </div>
      </div>

      {/* Import/Export */}
      <div className="flex gap-2 mt-2">
        <Button
          type="button"
          onClick={handleExportTrust}
          variant="outline"
          className="flex-1 h-9 rounded-full border border-border font-bold text-xs cursor-pointer"
        >
          Export Sources
        </Button>
        <label className="flex-1 cursor-pointer">
          <span className={`flex items-center justify-center h-9 rounded-full border border-border font-bold text-xs bg-secondary/20 hover:bg-secondary/40 transition-colors`}>
            Import Sources
          </span>
          <input
            type="file"
            accept=".json"
            onChange={handleImportTrust}
            className="hidden"
          />
        </label>
      </div>
    </div>
  );
}

export default SettingsPage;
